import { Card, CardBody } from "@progress/kendo-react-layout";
import React, { useEffect, useState } from "react";
import { CustomRowRender } from "ui/grid/CustomGrid";
import style from "./style.module.css";
import styles from "styles.module.css";
import { ICON_NAMES } from "utils/constant";
import { addMonths } from "date-fns";
import { Grid, GridCellProps, GridColumn } from "@progress/kendo-react-grid";
import { TextButton } from "ui/Button";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";
import { PageTypePath } from "utils/pages";
import { useUserOrganizationsStore } from "store";
import { releasedPackageFrontGridColumnItem } from "component/pages/releasePackage/GridColumns";
import { getReleasedPackagesApi } from "api/releasedPackage";
import { ReleasePackage } from "interface/package";
import { lastColWithBtn } from "ui/grid/lastColumnts";
import { GrideDateFormatter } from "ui/Date";

type ReleasePackageCardItem = ReleasePackage & Record<string, any>;

const getValue = <T,>(
	item: ReleasePackageCardItem,
	camelKey: string,
	pascalKey: string,
): T | undefined => {
	return (item[camelKey] ?? item[pascalKey]) as T | undefined;
};

const formatWeight = (value?: number | string) =>
	`${Number(value || 0).toLocaleString("no-NO", {
		maximumFractionDigits: 2,
	})} kg`;

const formatAmount = (value?: number | string) =>
	Number(value || 0).toLocaleString("no-NO", {
		maximumFractionDigits: 2,
		style: "currency",
		currency: "NOK",
	});

function ReleasePackageCard({
	isMobileView = false,
}: {
	isMobileView?: boolean;
}) {
	const { t } = useTranslation();
	const [gridData, setGridData] = useState<ReleasePackage[]>();
	const navigate = useNavigate();
	const { defaultOrg } = useUserOrganizationsStore();

	const openPdf = (prop: GridCellProps) => {
		const reportGuid = getValue<string>(
			prop.dataItem as ReleasePackageCardItem,
			"reportGuid",
			"ReportGuid",
		);

		if (!reportGuid) {
			return;
		}

		window.open(`/${PageTypePath.Pdf}/${reportGuid}`, "_blank");
	};

	useEffect(() => {
		getReleasedPackagesApi({
			take: 3,
			skip: 0,
			fromDate: addMonths(new Date(), -1),
			toDate: new Date(),
			terminalId: defaultOrg?.id as any,
		})
			.then((response) => {
				setGridData(response.data);
			})
			.catch((err) => {
				console.log(err);
			});
	}, [defaultOrg]);

	return (
		<div>
			<Card
				//className={style.CardItem}
				className={"home-card-item"}
			>
				<CardBody>
					<div className={`${styles.BoxHeadingMedium} ${style.CardBodyHeader}`}>
						<div>{t("LastReleasedPackages")}</div>
						<div>
							<TextButton
								text={t("OverviewReleasedPackages")}
								onClick={() => navigate(`/${PageTypePath.ReleasedPackages}`)}
								iconName={ICON_NAMES.Arrow}
							/>
						</div>
					</div>
					<div>
						{isMobileView ? (
							<div>
								{(gridData || []).map((item, index) => {
									const cardItem = item as ReleasePackageCardItem;
									const registrationId = getValue<number | string>(
										cardItem,
										"registrationId",
										"RegistrationId",
									);
									const confirmedTime = getValue<Date | string>(
										cardItem,
										"confirmedTime",
										"ConfirmedTime",
									);
									const liveWeightKg = getValue<number | string>(
										cardItem,
										"liveWeightKg",
										"LiveWeightKg",
									);
									const packageId = getValue<number | string>(
										cardItem,
										"packageId",
										"PackageId",
									);
									const buyerName = getValue<string>(
										cardItem,
										"buyerName",
										"BuyerName",
									);
									const vesselRegistrationId = getValue<string>(
										cardItem,
										"vesselRegistrationId",
										"VesselRegistrationId",
									);
									const vesselName = getValue<string>(
										cardItem,
										"vesselName",
										"VesselName",
									);
									const amount = getValue<number | string>(
										cardItem,
										"amount",
										"Amount",
									);
									const reportGuid = getValue<string>(
										cardItem,
										"reportGuid",
										"ReportGuid",
									);
									const vesselSummary = [vesselRegistrationId, vesselName]
										.filter((value) => value && `${value}`.trim().length > 0)
										.join(" - ");
									const registrationSummary = [registrationId, packageId]
										.filter(
											(value) =>
												value !== undefined &&
												value !== null &&
												`${value}`.trim().length > 0,
										)
										.join(" - ");

									return (
										<div
											key={`${registrationId ?? "noreg"}-${index}`}
											className="home-release-package-mobile-item"
										>
											<div className="home-release-package-mobile-grid">
												<div className="home-release-package-mobile-top-row">
													<div className="home-release-package-mobile-field home-release-package-mobile-field-wide">
														<div className="home-release-package-mobile-label">
															{t("VesselRegistrationId")} - {t("Vessel")}
														</div>
														<div className="home-release-package-mobile-value">
															{vesselSummary || "-"}
														</div>
													</div>
													<div className="home-release-package-mobile-actions-cell">
														<div className="home-mobile-list-item-actions">
															<button
																type="button"
																className="home-mobile-icon-btn"
																onClick={() =>
																	openPdf({ dataItem: item } as GridCellProps)
																}
																disabled={!reportGuid}
															>
																<img
																	src={`/icon/${ICON_NAMES.Download}.svg`}
																	alt={t("Download")}
																/>
															</button>
														</div>
													</div>
												</div>
												<div className="home-release-package-mobile-field home-release-package-mobile-field-full">
													<div className="home-release-package-mobile-label">
														{t("BuyerName")}
													</div>
													<div className="home-release-package-mobile-value">
														{buyerName || "-"}
													</div>
												</div>
												<div className="home-release-package-mobile-field">
													<div className="home-release-package-mobile-label">
														{t("RegistrationIdShort")} - {t("Package")}
													</div>
													<div className="home-release-package-mobile-value">
														{registrationSummary || "-"}
													</div>
												</div>
												<div className="home-release-package-mobile-field">
													<div className="home-release-package-mobile-label">
														{t("ConfirmTime")}
													</div>
													<div className="home-release-package-mobile-value">
														{confirmedTime ? (
															<GrideDateFormatter
																date={confirmedTime}
																formatType="dd.MM.yyyy"
																noTd={true}
															/>
														) : (
															"-"
														)}
													</div>
												</div>
												<div className="home-release-package-mobile-field">
													<div className="home-release-package-mobile-label">
														{t("Weight")}
													</div>
													<div className="home-release-package-mobile-value">
														{formatWeight(liveWeightKg)}
													</div>
												</div>
												<div className="home-release-package-mobile-field">
													<div className="home-release-package-mobile-label">
														{t("Value")}
													</div>
													<div className="home-release-package-mobile-value">
														{formatAmount(amount)}
													</div>
												</div>
											</div>
										</div>
									);
								})}
							</div>
						) : (
							<Grid
								style={{ height: "auto" }}
								rowHeight={38}
								rows={{ data: (row) => CustomRowRender(row) }}
								data={gridData}
								className={
									styles.tableColgroupColFirstChild + " custom_detail_grid"
								}
							>
								{releasedPackageFrontGridColumnItem().map((column, key) => (
									<GridColumn key={key} {...column} className="no-border" />
								))}

								<GridColumn
									{...lastColWithBtn({
										width: "100px",
										showDetails: false,
										items: [
											{
												iconName: ICON_NAMES.Download,
												alwaysShow: true,
												onclick: openPdf,
											},
										],
										showDelete: false,
									})}
								/>
							</Grid>
						)}
					</div>
				</CardBody>
			</Card>
		</div>
	);
}

export default ReleasePackageCard;
